// src/components/common/KeyboardShortcutsDialog.tsx
// Dialog affichant tous les raccourcis clavier disponibles
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Grid,
  IconButton,
  Divider,
  alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import { ShortcutGroup, formatShortcutKey } from '../../hooks/useKeyboardShortcuts';

interface KeyboardShortcutsDialogProps {
  open: boolean;
  onClose: () => void;
  shortcutGroups: ShortcutGroup[];
}

const KeyboardShortcutsDialog: React.FC<KeyboardShortcutsDialogProps> = ({
  open,
  onClose,
  shortcutGroups,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <KeyboardIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Raccourcis clavier
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Utilisez ces raccourcis pour naviguer rapidement dans l'application.
          Appuyez sur <Kbd>?</Kbd> pour afficher cette aide à tout moment.
        </Typography>

        <Grid container spacing={3}>
          {shortcutGroups.map((group) => (
            <Grid item xs={12} md={6} key={group.category}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'grey.50',
                  height: '100%',
                }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  color="primary"
                  sx={{ mb: 1.5 }}
                >
                  {group.category}
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {group.shortcuts.map((shortcut, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 0.5,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {shortcut.description}
                      </Typography>
                      <Kbd>{formatShortcutKey(shortcut)}</Kbd>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" color="text.disabled">
            Les raccourcis sont désactivés lorsque vous êtes dans un champ de saisie
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

// Composant pour afficher une touche stylisée
const Kbd: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      px: 1,
      py: 0.25,
      minWidth: 24,
      fontSize: '0.75rem',
      fontFamily: 'monospace',
      fontWeight: 600,
      color: 'text.primary',
      bgcolor: 'background.paper',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      boxShadow: (theme) => `0 2px 0 ${alpha(theme.palette.common.black, 0.1)}`,
    }}
  >
    {children}
  </Box>
);

export default KeyboardShortcutsDialog;
